'use strict';

import { observable, action, configure, makeObservable } from "mobx";
configure({enforceActions: 'always'});

import {Breakpoints, States} from "@uw-it-sis/lib-react/lib/AppConstants";
import events from "@uw-it-sis/lib-react/lib/Events";
import {ScheduleBuilderEvents} from "./ScheduleBuilderConstants";
import pinnedSchedulesStore from "./PinnedSchedulesStore";
import {buildEvents} from "./ScheduleBuilderUtils";
import scheduleBuilderOptionsStore from "./ScheduleBuilderOptionsStore";
import {getBreakpointSize, size} from "@uw-it-sis/lib-react/lib/Utils";
import appStore from "@uw-it-sis/lib-react/lib/AppStore";

class ScheduleBuilderOptionStore {

    state = States.initial;

    displayState = {};

    constructor() {
        makeObservable(this, {
            state: observable,
            displayState: observable,
            mount: action,
            togglePinned: action,
            toggleShowSchedule: action
        });

        this.schedule = {};

        this.pinnedSchedule = {};

        this.initialDate = undefined;

        this.optionIndex = -1;

        this.analyticsStore = appStore.getAnalyticsStore();
    }

    /**
     * Initialize the display state for a schedule builder option
     *
     * @param schedule the schedule data
     * @param optionIndex the index within the schedule options list ( undefined if it's a pinned schedule )
     * @param initialDate the first date of the option
     * @param defaultShow whether the option should be shown by default
     */
    mount(schedule, optionIndex, initialDate, defaultShow) {

        this.state = States.pending;

        this.initialDate = initialDate;
        this.optionIndex = optionIndex;

        let isPinned = pinnedSchedulesStore.isPinned(schedule.activityHash);
        let labelIndex = optionIndex;

        if (isPinned) {
            this.pinnedSchedule = pinnedSchedulesStore.getSchedulePinnedData(schedule.activityHash);
            labelIndex = this.pinnedSchedule.number;
        }

        this.schedule = schedule;

        this.displayState = {
            pinned: isPinned,
            shown: false,
            labelIndex: labelIndex,
            cssClasses: []
        };

        this.scheduleDisplayToggledListener = (eventObj) => {
            if (
                this.schedule.activityHash === eventObj.activityHash
                &&
                this.displayState.shown !== eventObj.shown
                &&
                this.displayState.pinned
            ) {
                this.displayState = Object.assign(this.displayState, {
                    shown: eventObj.shown,
                    cssClasses: (
                        eventObj.shown ?
                            [scheduleBuilderOptionsStore.getCssClass(this.displayState.pinned)]
                            :
                            []
                    )
                })
            }
        }

        events.listen(ScheduleBuilderEvents.scheduleDisplayToggled, (eventObj) => {
            this.scheduleDisplayToggledListener(eventObj);
        });

        this.pinnedSchedulesLoadedListener = (activityHashes) => {
            // If a schedule option is pinned or its pinned state has changed, we need to re-toggle the option to get correct labels/colors
            let reToggleSchedule = false;
            // was pinned, but is no longer in list of pinned items
            let pinnedRemoved = (this.displayState.pinned && activityHashes.indexOf(this.schedule.activityHash) < 0);
            // wasn't pinned, but is now in the list of pinned items
            let pinnedAdded = (!this.displayState.pinned && activityHashes.indexOf(this.schedule.activityHash) >= 0);
            // Pinned being shown
            let isDisplayedPinned = (this.displayState.pinned && this.displayState.shown);

            let hasPinnedChanged = (pinnedRemoved || pinnedAdded);

            // If schedule option is shown and is pinned or its pinned state has changed, it needs to be re-toggled
            if ( isDisplayedPinned || ( hasPinnedChanged && this.displayState.shown ) ) {
                reToggleSchedule = true;
                // turns schedule option being shown off
                this.toggleShowSchedule(false);
            }

            if (hasPinnedChanged) {
                this.displayState.pinned = !this.displayState.pinned;
            }

            if (this.displayState.pinned) {
                this.pinnedSchedule = pinnedSchedulesStore.getSchedulePinnedData(schedule.activityHash);
                this.displayState.labelIndex = this.pinnedSchedule.number;
            } else {
                this.pinnedSchedule = undefined;
                this.displayState.labelIndex = this.optionIndex;
            }

            if (reToggleSchedule) {
                // turns schedule option back on to be shown with updated labels/colors
                this.toggleShowSchedule(false);
            }
        }

        events.listen(ScheduleBuilderEvents.pinnedSchedulesLoaded, (activityHashes) => {
            this.pinnedSchedulesLoadedListener(activityHashes);
        });

        // if the card should be shown by default , toggle it on
        if (defaultShow) {
            this.toggleShowSchedule(false);
        }

        this.state = States.done;

    }

    unmount() {
        events.remove(ScheduleBuilderEvents.scheduleDisplayToggled, (eventObj) => {
            this.scheduleDisplayToggledListener(eventObj);
        });

        events.remove(ScheduleBuilderEvents.pinnedSchedulesLoaded, (activityHashes) => {
            this.pinnedSchedulesLoadedListener(activityHashes);
        });
    }

    togglePinned() {
        this.state = States.pending;

        let context = "SB-Link"
        if (this.displayState.pinned) {
            pinnedSchedulesStore.deletePinnedSchedule(this.pinnedSchedule.id, context);
        } else {
            pinnedSchedulesStore.addPinnedSchedule(this.schedule, context);
        }

        this.state = States.done;
    }

    toggleShowSchedule(generateEvent = true) {
        this.state = States.pending;

        let newShown = !this.displayState.shown;

        /**
         * Only generate an analytics click event when schedule option is being shown (selected) via user click; situations where we don't want event generated:
         * - when user deselects schedule to not show anymore
         * - schedule shown on mount, first one is by default
         * - when a user pins a schedule shown, the toggle is fired twice (off and back on) to clean up the view (change color and label)
         */
        if (generateEvent && newShown) {
            const currentSize = getBreakpointSize();
            let carouselItems = 5;
            if (currentSize === Breakpoints.md.code) {
                carouselItems = 4;
            }
            if (currentSize === Breakpoints.sm.code) {
                carouselItems = 3;
            }
            if (currentSize === Breakpoints.xs.code) {
                carouselItems = 2;
            }

            let totalSchedules = 0;
            if (scheduleBuilderOptionsStore.schedules) {
                totalSchedules = size(scheduleBuilderOptionsStore.schedules.possibleSchedules);
            }

            let selectionIndex = this.displayState.labelIndex;
            if (this.displayState.pinned) {
                selectionIndex = `P${this.displayState.labelIndex}`;
            }

            const scheduleAction = newShown ? "select" : "deselect";

            let additionalData = {
                scheduleSelection: selectionIndex,
                scheduleAction: scheduleAction,
                scheduleShown: carouselItems,
                scheduleTotal: totalSchedules
            }

            this.analyticsStore.addEvent("SB-option-click", {}, additionalData);
        }

        if (newShown && scheduleBuilderOptionsStore.getDisplayLimit() === 0 ) {
            scheduleBuilderOptionsStore.openDisplayLimitMessage();
        } else {
            let storeScheduleData = this._buildScheduleData();

            let cardCssClasses = [];
            if (newShown) {
                let cssClass = scheduleBuilderOptionsStore.getCssClass(this.displayState.pinned);
                storeScheduleData.classNames = [cssClass, "cursor-pointer"];
                cardCssClasses = [cssClass];
            }

            this.displayState.shown = newShown;
            this.displayState.cssClasses = cardCssClasses;

            scheduleBuilderOptionsStore.toggleDisplayedSchedule(newShown, storeScheduleData);
        }

        this.state = States.done;
    }

    _buildScheduleData() {

        let indicator = "" + this.displayState.labelIndex;
        let name = `Schedule Option ${this.displayState.labelIndex}`;

        if (this.displayState.pinned) {
            indicator = `P${this.displayState.labelIndex}`;
            name = `Pinned Schedule ${this.displayState.labelIndex}`;
        }

        return {
            id: this.schedule.id,
            activityHash: this.schedule.activityHash,
            events: buildEvents(this.schedule.activityOptions, this.initialDate, indicator),
            isPinned: this.displayState.pinned,
            isScheduleOption: true,
            name: name,
            classNames: []
        };
    }

}

export default ScheduleBuilderOptionStore;